// Разбор турецкого текста для перевода по наведению/нажатию
// (.scratch/tap-translate). Чистые функции — без DOM и сети, покрыты тестами.
// Импортов со значениями здесь нет намеренно: файл напрямую подключает
// scripts/warm-translations.mts (Node 24 с type stripping, без алиасов @/).
import type { SentenceGloss } from './deepseek'

// Буквы турецкого алфавита + â/î/û (циркумфлекс в заимствованиях: kâr, hâlâ).
const TR_LETTERS = 'A-Za-zÇçĞğİıÖöŞşÜüÂâÎîÛû'

// Слово — непрерывная латиница; апостроф внутри допускается, потому что
// аффиксы к именам собственным пишутся через него: İstanbul'da, Ali'nin.
const TR_WORD = new RegExp(`[${TR_LETTERS}]+(?:['’][${TR_LETTERS}]+)*`, 'g')

export type TextSegment = { text: string; word: boolean; start: number }

// Делит текст на слова (кликабельные) и всё остальное (пробелы, знаки,
// кириллица). Кириллица — родной язык ученика — словом не считается: только
// латиница, то есть турецкие вкрапления в русском ответе тоже размечаются.
export function segmentText(text: string): TextSegment[] {
  const out: TextSegment[] = []
  let last = 0
  for (const m of text.matchAll(TR_WORD)) {
    const start = m.index ?? 0
    if (start > last) out.push({ text: text.slice(last, start), word: false, start: last })
    out.push({ text: m[0], word: true, start })
    last = start + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last), word: false, start: last })
  return out
}

// Турецкий нижний регистр: I → ı, İ → i (обычный toLowerCase дал бы I → i,
// и «IRMAK» не совпал бы со словарным «ırmak»).
export function trLower(s: string): string {
  return s.toLocaleLowerCase('tr-TR')
}

// Нормализация для сравнения со словарём и для ключа кэша: турецкий lowercase,
// типографский апостроф → обычный, схлопнутые пробелы, без крайних знаков.
export function normalizeTr(s: string): string {
  return trLower(s.replace(/’/g, "'").replace(/\s+/g, ' ').trim()).replace(/^[\s"«“'.,!?:;()—–-]+|[\s"»”'.,!?:;()—–-]+$/g, '')
}

const SENTENCE_END = /[.!?…\n]/

// Предложение вокруг позиции (для перевода слова в контексте). Длинные
// куски обрезаются — модели хватает ближайшего окружения.
export function sentenceAround(text: string, index: number, maxLen = 300): string {
  let start = index
  while (start > 0 && !SENTENCE_END.test(text[start - 1])) start--
  let end = index
  while (end < text.length && !SENTENCE_END.test(text[end])) end++
  if (end < text.length && text[end] !== '\n') end++ // включить сам знак конца
  let sentence = text.slice(start, end).trim()
  if (sentence.length > maxLen) {
    const rel = index - start
    const from = Math.max(0, rel - maxLen / 2)
    sentence = sentence.slice(from, from + maxLen).trim()
  }
  return sentence
}

export type TranslateKind = 'word' | 'phrase'

export function cacheKey(kind: TranslateKind, text: string, context = ''): string {
  return `${kind}|${normalizeTr(text)}|${kind === 'word' ? normalizeTr(context) : ''}`
}

export type CachedTranslation = {
  kind: TranslateKind
  translation: string
  lemma: string | null
  lemmaTranslation: string | null
  breakdown: { part: string; meaningRu: string }[]
}

export type CacheRow = { key: string; kind: TranslateKind; source: string; context: string; result: CachedTranslation }

// Ответ модели на всё предложение → строки translation_cache: одна на фразу
// целиком и по одной на каждое слово (в контексте этого предложения).
// Повторы одного слова в фразе схлопываются — ключ одинаковый.
export function glossToCacheRows(sentence: string, gloss: SentenceGloss): CacheRow[] {
  const rows = new Map<string, CacheRow>()
  const phraseKey = cacheKey('phrase', sentence)
  rows.set(phraseKey, {
    key: phraseKey,
    kind: 'phrase',
    source: sentence,
    context: '',
    result: { kind: 'phrase', translation: gloss.translation, lemma: null, lemmaTranslation: null, breakdown: [] },
  })
  for (const w of gloss.words) {
    if (!w.word || !hasTurkish(w.word)) continue
    const key = cacheKey('word', w.word, sentence)
    rows.set(key, {
      key,
      kind: 'word',
      source: w.word,
      context: sentence,
      result: { kind: 'word', translation: w.translation, lemma: w.lemma, lemmaTranslation: w.lemmaTranslation, breakdown: w.breakdown },
    })
  }
  return [...rows.values()]
}

// Есть ли в строке вообще что переводить (хотя бы одно турецкое слово).
export function hasTurkish(text: string): boolean {
  return new RegExp(`[${TR_LETTERS}]`).test(text)
}

// Текст для Google TTS: строчная i заменяется на турецкую İ.
// Голоса Chirp3-HD читают слово латиницей по-английски, если оно похоже на
// английское: «size» (вам) звучало как «сайз» (проверено на слух 2026-09-30).
// Одна турецкая буква в слове переключает модель на турецкое чтение, а звук
// не меняется: İ и i — один и тот же гласный /i/. Проверено на «bir», «kim»,
// «için», «gitti» и на длинных фразах: звучат так же, как раньше.
// Слово в базе и на экране остаётся прежним, меняется только текст запроса.
export function ttsDottedI(text: string): string {
  return text.replace(/i/g, 'İ')
}

// ── Словарь по словоформе из кэша (решение пользователя 03.10) ───────────────
// Ключ кэша слова включает фразу, и знакомая форма в новой фразе шла бы к
// модели. Если у формы в кэше два и больше разборов из разных фраз и все
// сходятся в начальной форме и переводе, слово устойчивое — отдаём его без
// модели. Расходятся (yüz: лицо / сто, kar: снег / прибыль) — слово зависит от
// контекста, нужна модель. Сравнение без учёта регистра, кавычек и знаков.
const same = (s: string | null | undefined) =>
  (s ?? '')
    .toLocaleLowerCase('ru')
    .replace(/["«»“”'`]/g, '')
    .replace(/[.!?,;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export function consensusOf(rows: CachedTranslation[]): CachedTranslation | null {
  const words = rows.filter((r) => r.kind === 'word' && r.translation)
  if (words.length < 2) return null
  const [first, ...rest] = words
  const agree = rest.every((r) => same(r.translation) === same(first.translation) && normalizeTr(r.lemma ?? '') === normalizeTr(first.lemma ?? ''))
  return agree ? first : null
}

// ── Перевод по тапу в любом тексте (владелец 16.10.2026) ──
// На телефоне тап по турецкому слову где угодно открывает подсказку, не только
// в <TrText>. Обработчик в components/tr-text.tsx (TrPopoverHost) находит
// текст под пальцем, а эти функции решают, что там за слово и можно ли его
// переводить.

// Латиница в интерфейсе, которая не турецкий текст: переводить её незачем.
const NOT_TURKISH = new Set([
  'turkido', 'plus', 'pro', 'telegram', 'google', 'yandex', 'deepseek', 'glif', 'ai', 'ok', 'id',
  'iphone', 'android', 'whatsapp', 'pwa', 'utm', 'flux', 'cloudflare', 'max', 'faq', 'vk', 'email', 'mail', 'gmail',
  'online', 'push', 'safari', 'chrome', 'windows', 'mac', 'ios', 'app', 'store',
])

// Слово, в которое попадает позиция index (в том числе на его правом краю).
// Одна буква (разбор гармонии: "после i пишут i") и служебная латиница не в счёт.
export function wordAt(text: string, index: number): { word: string; start: number } | null {
  for (const m of text.matchAll(TR_WORD)) {
    const start = m.index ?? 0
    if (start > index) break
    if (index <= start + m[0].length) {
      const word = m[0]
      if (word.length < 2 || NOT_TURKISH.has(trLower(word))) return null
      return { word, start }
    }
  }
  return null
}

// Экраны-задания, где тап по слову выдал бы ответ: повторение, игры, тренажёры
// грамматики. Урок помечает такие шаги сам (data-no-translate в lesson-player).
const QUIZ_PATHS = [/^\/review\/?$/, /^\/review\/grammar/, /^\/games\/.+/, /^\/grammar\/(game|train|warmup|weak|review)/]
export function isQuizPath(pathname: string): boolean {
  return QUIZ_PATHS.some((re) => re.test(pathname))
}
